import { useState } from "react";
import { Link } from "react-router";

import classes from "./ChemicalPeels.module.css";
import { treatmentPages, servicesData } from "./serviceData";
import BookingWidgetSection from "../../Components/BookingWidget/BookingWidgetSection";
import useDocumentMeta from "../../utils/useDocumentMeta";
import useSectionView from "../../utils/useSectionView";
import {
  BOOKING_URL,
  trackBookingClick,
  trackFaqToggle,
} from "../../utils/tracking";

import heroImage from "../../Assets/chemical-peels-top.jpg";
import roomImage from "../../Assets/ra_mood.jpg";
import ledImage from "../../Assets/dermalux_treatment.jpg";

const page = treatmentPages.find((p) => p.slug === "chemical-peels");
const peelService = servicesData.chemicalPeels.find(
  (s) => s.id === "biorepeel",
);

const benefits = [
  {
    icon: "🎯",
    title: "Acne & Scarring",
    text: "Helps refine texture and calm active breakouts.",
  },
  {
    icon: "✨",
    title: "Pigmentation",
    text: "Targets discolouration for a more even tone.",
  },
  {
    icon: "〰️",
    title: "Fine Lines & Wrinkles",
    text: "Stimulates collagen for smoother-looking skin.",
  },
  {
    icon: "🔬",
    title: "Large Pores & Texture",
    text: "Refines pores and evens out rough texture.",
  },
];

const journeySteps = [
  {
    title: "Cleanse & Steam",
    text: "Your treatment begins with a thorough cleanse and steam to prepare the skin for the peel.",
  },
  {
    title: "Biorepeel Application",
    text: "A 2-phase peel combining organic acids, enzymes and vitamins is applied to gently exfoliate the skin, removing dead skin cells and impurities while stimulating collagen production.",
  },
  {
    title: "Hydrating Mask & Chest Massage",
    text: "A hydrating mask is applied to soothe the skin, alongside a relaxing chest massage.",
  },
  {
    title: "Dermalux LED & Vitamins",
    text: "The treatment finishes with Dermalux light therapy plus vitamins and minerals to nourish and protect.",
  },
];

const expectCards = [
  {
    when: "During Treatment",
    text: "Most clients experience only mild tingling — this peel is formulated to suit all skin types.",
  },
  {
    when: "Immediately After",
    text: "Minimal to no downtime. Skin may feel slightly warm or tight, but visible peeling is uncommon.",
  },
  {
    when: "Ongoing",
    text: "Many clients notice improvement after a single session. For deeper concerns like scarring or pigmentation, a short course is often recommended.",
  },
];

const aftercareBullets = [
  "No exercising or sweating for 12 hours after treatment",
  "Avoid swimming, saunas or similar spa environments for 3 days",
  "Sleep on a clean, fresh pillowcase — silk if you have one",
  "Avoid glycolic acid, salicylic acid, scrubs and exfoliators for 3–5 days",
  "Wait 24 hours before applying makeup, and use clean, sanitised brushes",
  "Leave at least 2 weeks between chemical peel treatments",
];

const faqs = [
  {
    q: "Is a chemical peel painful?",
    a: "No — most clients describe only a mild tingling sensation during application. The Biorepeel formula is designed to be comfortable, with minimal to no downtime afterwards.",
  },
  {
    q: "How many sessions will I need?",
    a: "Many clients see an improvement after just one session. For deeper concerns like acne scarring or stubborn pigmentation, a short course of treatments is often recommended — we'll advise what's right for your skin at your consultation.",
  },
  {
    q: "Is it suitable for my skin type?",
    a: "Biorepeel is formulated to suit all skin types, including sensitive skin, and is effective for acne, scarring, pigmentation, fine lines, large pores and uneven texture.",
  },
  {
    q: "Can I combine this with other treatments?",
    a: "Leave at least 2 weeks between a chemical peel and any other facial treatment to allow your skin to fully recover — we'll help you plan this during your consultation.",
  },
];

const ChemicalPeelsPage = () => {
  const [expandedFaq, setExpandedFaq] = useState(null);
  const faqSectionRef = useSectionView("Chemical Peels FAQ");

  useDocumentMeta(
    `${page.title} | Radiance Aesthetics | Hindley | Wigan`,
    page.metaDescription,
  );

  return (
    <>
      {/* Hero */}
      <section className={classes.heroSection}>
        <div className={classes.heroContainer}>
          <div className={classes.heroContent}>
            <p className={classes.heroSubtitle}>Nurse-Led · Medical-Grade</p>
            <h1 className={classes.heroTitle}>Chemical Peels</h1>
            <p className={classes.heroMeta}>
              <span>From £{peelService.price}</span> · {peelService.duration}
            </p>
            <p className={classes.heroDescription}>
              A 2-phase Biorepeel treatment for acne, scarring, pigmentation,
              fine lines and uneven texture — delivered as a full, relaxing
              facial experience with minimal to no downtime.
            </p>
            <a
              href={BOOKING_URL}
              className={classes.heroCta}
              target="_blank"
              rel="noreferrer"
              onClick={() => trackBookingClick("Chemical Peels Hero")}
            >
              Book Now
            </a>
          </div>
          <div className={classes.heroImageWrapper}>
            <img
              className={classes.heroImage}
              src={heroImage}
              alt="Client receiving a chemical peel facial at Radiance Aesthetics Wigan"
              loading="eager"
            />
          </div>
        </div>
      </section>

      {/* Highlight banner */}
      <section className={classes.section}>
        <div className={classes.highlightBannerWrapper}>
          <div className={classes.highlightBanner}>
            <h2 className={classes.highlightTitle}>More Than Just a Peel</h2>
            <p className={classes.highlightText}>
              Every Chemical Peel here is a complete facial experience built
              around the peel step — not just the peel itself. Your session
              includes a <strong>cleanse and steam</strong>, the{" "}
              <strong>Biorepeel application</strong>, a{" "}
              <strong>hydrating mask with chest massage</strong>, and a
              finishing <strong>Dermalux LED light therapy</strong> session with
              vitamins and minerals — all included in your appointment.
            </p>
          </div>
        </div>
      </section>

      {/* What it treats */}
      <section className={`${classes.section} ${classes.sectionAlt}`}>
        <div className={classes.sectionContainer}>
          <p className={classes.sectionLabel}>What It Treats</p>
          <h2 className={classes.sectionTitle}>Suitable For</h2>
          <p className={classes.sectionDescription}>
            Biorepeel is effective for a range of common skin concerns, and is
            formulated to suit all skin types.
          </p>
          <div className={classes.benefitGrid}>
            {benefits.map((b) => (
              <div className={classes.benefitCard} key={b.title}>
                <div className={classes.benefitIcon} aria-hidden="true">
                  {b.icon}
                </div>
                <h3 className={classes.benefitTitle}>{b.title}</h3>
                <p className={classes.benefitText}>{b.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Journey */}
      <section className={classes.section}>
        <div className={classes.sectionContainer}>
          <p className={classes.sectionLabel}>Step By Step</p>
          <h2 className={classes.sectionTitle}>Your Treatment Journey</h2>
          <p className={classes.sectionDescription}>
            Every Chemical Peel follows the same relaxing, thorough process from
            start to finish.
          </p>
          <div className={classes.journeyList}>
            {journeySteps.map((step, i) => (
              <div className={classes.journeyStep} key={step.title}>
                <div className={classes.journeyNumber}>{i + 1}</div>
                <div className={classes.journeyContent}>
                  <h3 className={classes.journeyTitle}>{step.title}</h3>
                  <p className={classes.journeyText}>{step.text}</p>
                </div>
              </div>
            ))}
          </div>
          <div className={classes.journeyImages}>
            <img
              className={classes.journeyImage}
              src={roomImage}
              alt="Treatment room and equipment at Radiance Aesthetics"
              loading="lazy"
            />
            <img
              className={classes.journeyImage}
              src={ledImage}
              alt="Client under the Dermalux LED light therapy device included in every chemical peel"
              loading="lazy"
              style={{ objectPosition: "center 40%" }}
            />
          </div>
        </div>
      </section>

      {/* What to expect */}
      <section className={`${classes.section} ${classes.sectionAlt}`}>
        <div className={classes.sectionContainer}>
          <p className={classes.sectionLabel}>Downtime & Results</p>
          <h2 className={classes.sectionTitle}>What to Expect</h2>
          <div className={classes.expectGrid}>
            {expectCards.map((c) => (
              <div className={classes.expectCard} key={c.when}>
                <p className={classes.expectWhen}>{c.when}</p>
                <p className={classes.expectText}>{c.text}</p>
              </div>
            ))}
          </div>
          <p className={classes.expectNote}>
            Individual results vary. We'll talk through what to expect for your
            specific skin concern at your consultation.
          </p>
        </div>
      </section>

      {/* Book this treatment */}
      <section className={classes.section}>
        <div className={classes.sectionContainer}>
          <p className={classes.sectionLabel}>Book This Treatment</p>
          <h2 className={classes.sectionTitle}>{peelService.name}</h2>
          <div className={classes.serviceCard}>
            <p className={classes.servicePrice}>£{peelService.price}</p>
            <p className={classes.serviceDuration}>
              {peelService.duration} · Full facial included
            </p>
            <ul className={classes.serviceHighlights}>
              <li>
                2-phase Biorepeel with organic acids, enzymes and vitamins
              </li>
              <li>Suitable for all skin types</li>
              <li>Hydrating mask, chest massage & Dermalux LED</li>
              <li>Minimal to no downtime</li>
            </ul>
            <a
              href={BOOKING_URL}
              className={classes.heroCta}
              target="_blank"
              rel="noreferrer"
              onClick={() => trackBookingClick("Chemical Peels Service Card")}
            >
              Book This Treatment
            </a>
          </div>
        </div>
      </section>

      {/* Aftercare */}
      <section className={`${classes.section} ${classes.sectionAlt}`}>
        <div className={classes.sectionContainer}>
          <p className={classes.sectionLabel}>Aftercare</p>
          <h2 className={classes.sectionTitle}>Caring For Your Skin After</h2>
          <div className={classes.aftercareCard}>
            <ul className={classes.aftercareList}>
              {aftercareBullets.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
            <Link to="/aftercare" className={classes.aftercareLink}>
              Read Full Aftercare Guide →
            </Link>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className={classes.section} ref={faqSectionRef}>
        <div className={classes.sectionContainer}>
          <p className={classes.sectionLabel}>FAQ</p>
          <h2 className={classes.sectionTitle}>Common Questions</h2>
          <div className={classes.faqContainer}>
            {faqs.map((faq, i) => (
              <div className={classes.faqItem} key={faq.q}>
                <h3
                  className={classes.faqQuestion}
                  onClick={() => {
                    const opening = expandedFaq !== i;
                    trackFaqToggle(faq.q, i, opening);
                    setExpandedFaq(opening ? i : null);
                  }}
                  role="button"
                  aria-expanded={expandedFaq === i}
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      const opening = expandedFaq !== i;
                      trackFaqToggle(faq.q, i, opening);
                      setExpandedFaq(opening ? i : null);
                    }
                  }}
                >
                  {faq.q} {expandedFaq === i ? "−" : "+"}
                </h3>
                {expandedFaq === i && (
                  <p className={classes.faqAnswer}>{faq.a}</p>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className={classes.ctaSection} aria-label="Book appointment">
        <h2 className={classes.ctaTitle}>Ready to Reveal Brighter Skin?</h2>
        <p className={classes.ctaDescription}>
          Book your Chemical Peel today, or get in touch for a free consultation
          to discuss your skin goals.
        </p>
        <a
          href={BOOKING_URL}
          className={classes.ctaButton}
          target="_blank"
          rel="noreferrer"
          onClick={() => trackBookingClick("Chemical Peels Bottom CTA")}
        >
          Book Now
        </a>
      </section>

      <div className={classes.ctaWidgetWrapper}>
        <BookingWidgetSection source="Treatments - Chemical Peels" />
      </div>
    </>
  );
};

export default ChemicalPeelsPage;
